import { Button } from '@1trading/react/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Textarea } from '@1trading/react/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BINARY_ANSWERS } from '@/constants/answers';
import { InstitutionFieldCountry } from '@/features/institution/components/fields/country';
import { INSTITUTION_ACTIVE_JURISDICTION_DEFAULTS, type InstitutionRegulatoryInformationOrDefaults } from '@/features/institution/regulatory-information/schema';

const InstitutionRegulatoryInformationFieldsetJurisdictions = () => {
  const form = useFormContext<InstitutionRegulatoryInformationOrDefaults>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'activeJurisdictions' });
  const { t } = useTranslation();
  const amlEquivalentLegislation = form.watch('amlEquivalentLegislation');

  return (
    <div className="space-y-6">
      <fieldset className="space-y-4">
        <legend className="font-medium">{t('institutionDetails.regulatoryInformation.jurisdictions.title')}</legend>
        <FormField
          control={form.control}
          name="activeJurisdictions"
          render={({ fieldState: { error } }) => (
            <FormItem>
              <div className="space-y-6">
                {fields.map((jurisdiction, index) => (
                  <div key={jurisdiction.id} className="grid grid-cols-2 gap-x-4 gap-y-6 border-b pb-6">
                    <div className="col-span-2">
                      <InstitutionFieldCountry<InstitutionRegulatoryInformationOrDefaults>
                        label={t('institutionDetails.regulatoryInformation.jurisdictions.country')}
                        name={`activeJurisdictions.${index}.country`}
                        placeholder={t('institutionDetails.regulatoryInformation.placeholders.activeJurisdictionCountry')}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name={`activeJurisdictions.${index}.licensedEntities`}
                      render={({ field }) => (
                        <FormItem className="col-span-2">
                          <FormLabel>{t('institutionDetails.regulatoryInformation.jurisdictions.licensedEntities')}*</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="no-spinner"
                              inputMode="numeric"
                              placeholder={t('institutionDetails.regulatoryInformation.placeholders.activeJurisdictionLicensedEntities')}
                              type="number"
                              value={field.value ?? ''}
                              onChange={(event) => field.onChange(event.target.value === '' ? undefined : Number(event.target.value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {fields.length > 1 ? (
                      <Button
                        className="col-span-2 justify-self-end gap-2 text-destructive hover:text-destructive"
                        size="sm"
                        type="button"
                        variant="ghost"
                        onClick={() => remove(index)}
                      >
                        <Trash2 className="size-4" />
                        {t('institutionDetails.regulatoryInformation.jurisdictions.remove')}
                      </Button>
                    ) : null}
                  </div>
                ))}
                <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append({ ...INSTITUTION_ACTIVE_JURISDICTION_DEFAULTS })}>
                  <Plus className="size-4" />
                  {t('institutionDetails.regulatoryInformation.jurisdictions.add')}
                </Button>
              </div>
              {error?.root?.message || error?.message ? <FormMessage>{error.root?.message ?? error.message}</FormMessage> : null}
            </FormItem>
          )}
        />
      </fieldset>
      <FormField
        control={form.control}
        name="amlEquivalentLegislation"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.regulatoryInformation.amlEquivalentLegislation')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.regulatoryInformation.amlEquivalentLegislation')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {amlEquivalentLegislation === 'YES' ? (
        <FormField
          control={form.control}
          name="applicableAmlLaws"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.regulatoryInformation.applicableAmlLaws')}*</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder={t('institutionDetails.regulatoryInformation.placeholders.applicableAmlLaws')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </div>
  );
};
InstitutionRegulatoryInformationFieldsetJurisdictions.displayName = 'InstitutionRegulatoryInformationFieldsetJurisdictions';

export { InstitutionRegulatoryInformationFieldsetJurisdictions };
